import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useDebouncedCallback } from "./useDebouncedCallback";

interface UseSearchInputOptions {
  defaultValue?: string;
  debounce?: number;
  onSearch?: (searchQuery: string) => void | Promise<void>;
  onReset?: () => void;
}

const DEFAULT_DEBOUNCE_TIME = 300;

export const useSearchInput = ({
  defaultValue = "",
  debounce = DEFAULT_DEBOUNCE_TIME,
  onSearch,
  onReset,
}: UseSearchInputOptions = {}) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [searchQuery, setSearchQuery] = useState(defaultValue);

  const search = useCallback(
    (query: string) => {
      setSearchQuery(query);
      onSearch?.(query);
    },
    [onSearch]
  );

  const debouncedSearch = useDebouncedCallback(search, debounce);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setInputValue(value);
    debouncedSearch(value.trim());
  };

  const reset = useCallback(() => {
    setInputValue("");
    debouncedSearch("");
    onReset?.();
  }, [debouncedSearch, onReset]);

  const inputProps = {
    value: inputValue,
    onChange,
  };

  const hasSearchQuery = searchQuery.length > 0;
  const hasInputValue = inputValue.length > 0;

  // Reset input value and search query when defaultValue changes
  useEffect(() => {
    setInputValue(defaultValue);
    setSearchQuery(defaultValue);
  }, [defaultValue]);

  return {
    searchQuery,
    hasSearchQuery,
    hasInputValue,
    inputProps,
    reset,
  };
};
